import { unwrap } from "@sagot/api-client";
import { SALES_REQUEST_NOTES_MAX, SALES_REQUEST_SEATS_MAX, type SalesRequestSourceKey } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, Dialog, DialogContent, EmptyState, ErrorState, Field, Icon, Input, RadioGroup, Select, Skeleton, Textarea, toast } from "@sagot/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import { useApi } from "../../../session/session-provider";
import { useMe } from "../../../session/use-me";
import { useWorkspaceId } from "../../../session/workspace-context";
import { useAddonCatalog, wsKeys, type AddonKey, type PlanKeyOut, type SalesRequestOut } from "../shared";
import { useBillingPlans } from "./use-billing";

const PHONE = /^\+?[0-9 ()-]{7,20}$/;
const EMAIL = /^[^@\s]{1,64}@[^@\s]+\.[^@\s]{2,}$/;
const NO_PLAN = "none";

/** Where the form was opened from, and what it starts with. */
export interface SalesRequestStart {
  source: SalesRequestSourceKey;
  planKey?: PlanKeyOut | null;
  cycle?: "monthly" | "annual";
}

const schema = z
  .object({
    plan: z.string(),
    addons: z.array(z.string()),
    seats: z.string(),
    cycle: z.enum(["monthly", "annual"]),
    notes: z.string().max(SALES_REQUEST_NOTES_MAX),
    name: z.string(),
    email: z.string(),
    phone: z.string(),
  })
  .superRefine((value, ctx) => {
    const fail = (path: string, message: string) => { ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message }); };
    if (!value.name.trim()) fail("name", "billing.salesRequest.errors.name");
    if (!EMAIL.test(value.email.trim())) fail("email", "billing.salesRequest.errors.email");
    if (value.phone.trim() && !PHONE.test(value.phone.trim())) fail("phone", "billing.salesRequest.errors.phone");
    const seats = Number(value.seats);
    if (value.seats.trim() && !(Number.isInteger(seats) && seats >= 1 && seats <= SALES_REQUEST_SEATS_MAX)) fail("seats", "billing.salesRequest.errors.seats");
    if (value.plan === NO_PLAN && value.addons.length === 0 && !value.notes.trim()) fail("notes", "billing.salesRequest.errors.nothing");
  });
type FormValues = z.infer<typeof schema>;

function useSendRequest(start: SalesRequestStart, onSent: (sent: SalesRequestOut) => void) {
  const api = useApi();
  const wsId = useWorkspaceId();
  const queryClient = useQueryClient();
  const message = useErrorMessage();
  return useMutation({
    mutationFn: (values: FormValues) =>
      unwrap(
        api.POST("/v1/billing/sales-requests", {
          body: {
            source: start.source,
            plan_key: values.plan === NO_PLAN ? null : (values.plan as PlanKeyOut),
            addons: values.addons.map((key) => ({ key: key as AddonKey, quantity: 1 })),
            seats: values.seats.trim() ? Number(values.seats) : null,
            billing_cycle: values.cycle,
            notes: values.notes.trim() || null,
            contact_name: values.name.trim(),
            contact_email: values.email.trim(),
            contact_phone: values.phone.trim() || null,
          },
        }),
      ),
    onSuccess: async (sent) => {
      await queryClient.invalidateQueries({ queryKey: wsKeys.salesRequests(wsId) });
      onSent(sent);
    },
    onError: (caught) => toast.error(message(caught)),
  });
}

/** The designed confirmation: the request is stored whatever happens to the notice e-mail. */
function Sent({ sent, email, onClose }: { sent: SalesRequestOut; email: string; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div data-testid="sales-request-sent">
      <EmptyState
        icon="check-circle"
        headingLevel={3}
        title={t("billing.salesRequest.sent.title")}
        description={t("billing.salesRequest.sent.body", { email })}
        action={<Button onClick={onClose}>{t("billing.salesRequest.sent.done")}</Button>}
      />
      <p className="text-center text-xs font-semibold text-fg-muted" data-testid="sales-request-reference">
        {t("billing.salesRequest.sent.reference", { number: sent.number })}
      </p>
      {sent.emailed ? null : (
        <p className="mt-2 flex items-start justify-center gap-2 text-center text-xs text-fg-muted" role="status">
          <Icon name="info" className="mt-0.5 size-4 shrink-0" />
          {t("billing.salesRequest.sent.noMail")}
        </p>
      )}
    </div>
  );
}

function Choices({ form }: { form: ReturnType<typeof useForm<FormValues>> }) {
  const { t } = useTranslation();
  const plans = useBillingPlans();
  const catalog = useAddonCatalog();
  const [plan, addons, cycle] = useWatch({ control: form.control, name: ["plan", "addons", "cycle"] });
  const recurring = (catalog.data ?? []).filter((addon) => addon.type === "recurring");
  if (plans.isError || catalog.isError) {
    return <ErrorState headingLevel={3} title={t("billing.salesRequest.loadError")} onRetry={() => { void plans.refetch(); void catalog.refetch(); }} retryLabel={t("common.retry")} />;
  }
  if (!plans.data || !catalog.data) {
    return (
      <div className="space-y-3" aria-hidden="true" data-testid="sales-request-loading">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  return (
    <>
      <Field label={t("billing.salesRequest.plan")}>
        <Select value={plan} onChange={(event) => { form.setValue("plan", event.target.value); }}>
          <option value={NO_PLAN}>{t("billing.salesRequest.planNone")}</option>
          {plans.data.map((item) => (
            <option key={item.key} value={item.key}>
              {item.name}
            </option>
          ))}
        </Select>
      </Field>
      {recurring.length > 0 ? (
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold">{t("billing.salesRequest.addons")}</legend>
          <p className="text-xs text-fg-muted">{t("billing.salesRequest.addonsHint")}</p>
          {recurring.map((addon) => (
            <Checkbox
              key={addon.key}
              label={addon.name}
              checked={addons.includes(addon.key)}
              onCheckedChange={(checked) => { form.setValue("addons", checked === true ? [...addons, addon.key] : addons.filter((key) => key !== addon.key)); }}
            />
          ))}
        </fieldset>
      ) : null}
      <div>
        <p className="mb-2 text-sm font-semibold" id="sr-cycle">
          {t("billing.salesRequest.cycle")}
        </p>
        <RadioGroup
          aria-labelledby="sr-cycle"
          variant="cards"
          className="grid-cols-2"
          value={cycle}
          onValueChange={(value) => { form.setValue("cycle", value); }}
          options={[
            { value: "monthly", label: t("billing.salesRequest.monthly") },
            { value: "annual", label: t("billing.salesRequest.annual") },
          ]}
        />
      </div>
    </>
  );
}

/**
 * "Contact sales" / "Pay manually / talk to sales" (DEC-155): plan or add-ons wanted, seats, cycle,
 * notes and a contact. The request is stored and e-mailed to sales; this dialog then shows a
 * confirmation with the reference.
 */
export function SalesRequestDialog({ start, onClose }: { start: SalesRequestStart; onClose: () => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const [sent, setSent] = useState<SalesRequestOut | null>(null);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { plan: start.planKey ?? NO_PLAN, addons: [], seats: "", cycle: start.cycle ?? "monthly", notes: "", name: me.data?.user.name ?? "", email: me.data?.user.email ?? "", phone: "" },
  });
  const send = useSendRequest(start, setSent);
  const errors = form.formState.errors;
  const err = (key: keyof FormValues) => (errors[key]?.message ? t(errors[key].message) : undefined);
  const submit = form.handleSubmit((values) => { send.mutate(values); });
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t("billing.salesRequest.title")}
        description={sent ? undefined : t("billing.salesRequest.subtitle")}
        closeLabel={t("common.close")}
        footer={
          sent ? undefined : (
            <>
              <Button variant="secondary" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" form="sr-form" loading={send.isPending}>
                <Icon name="send" />
                {t("billing.salesRequest.send")}
              </Button>
            </>
          )
        }
      >
        {sent ? (
          <Sent sent={sent} email={form.getValues("email")} onClose={onClose} />
        ) : (
          <form id="sr-form" className="space-y-4" onSubmit={(event) => void submit(event)} noValidate>
            <Choices form={form} />
            <Field label={t("billing.salesRequest.seats")} hint={t("billing.salesRequest.seatsHint")} error={err("seats")}>
              <Input inputMode="numeric" {...form.register("seats")} />
            </Field>
            <Field label={t("billing.salesRequest.notes")} hint={t("billing.salesRequest.notesHint")} error={err("notes")}>
              <Textarea rows={3} maxLength={SALES_REQUEST_NOTES_MAX} {...form.register("notes")} />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t("billing.salesRequest.contactName")} error={err("name")}>
                <Input autoComplete="name" {...form.register("name")} />
              </Field>
              <Field label={t("billing.salesRequest.contactEmail")} error={err("email")}>
                <Input type="email" autoComplete="email" {...form.register("email")} />
              </Field>
            </div>
            <Field label={t("billing.salesRequest.contactPhone")} error={err("phone")}>
              <Input type="tel" autoComplete="tel" {...form.register("phone")} />
            </Field>
            {send.isError ? (
              <p className="flex items-start gap-2 rounded-md bg-danger-soft p-3 text-sm text-danger" role="alert">
                <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0" />
                {t("billing.salesRequest.sendError")}
              </p>
            ) : null}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** The workspace's own requests, newest first (shown only once it has made one). */
export function useSalesRequests() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.salesRequests(wsId), queryFn: () => unwrap(api.GET("/v1/billing/sales-requests")), staleTime: 30_000 });
}
